import { Context, createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MasterOrderInfo, UpdatedSlaveConfig } from '../../../proto/trade-copier_pb';
import { useSimulateTrade } from './useSimulateTrade';
import { composeOrderType, MARKET_KIND, ORDER_SIDES } from './constants';

const DEFAULT_MASTER_ORDER: MasterOrderInfo.AsObject = {
  orderType: composeOrderType(ORDER_SIDES[0], MARKET_KIND),
  symbol: '',
  lots: 0,
  openPrice: 0,
  takeProfit: 0,
  stopLoss: 0,
  magicNumber: 0,
  placedType: '',
  expiration: '',
  comment: '',
  isPendingFill: false
};

/** What the route strip and placed-type options need to know about an account. */
export interface SimulateAccount {
  /** Platform code as stored, e.g. 'MT-4', 'CTR'. */
  platform: string;
  accountNumber: string;
}

interface SimulateMeta {
  masterAccountId: string;
  slaveAccountId: string;
  /** Slave config row id — required to persist a mapping after simulate. */
  slaveId?: string;
  masterSymbolOptions?: string[];
  masterAccount?: SimulateAccount;
  slaveAccount?: SimulateAccount;
  getSlaveConfigDraft: () => UpdatedSlaveConfig.AsObject;
}

interface SimulateState {
  isOpen: boolean;
  masterOrder: MasterOrderInfo.AsObject;
  customSlaveSymbol: string;
  /** Bumped by reset so uncontrolled inputs (symbol pickers) remount empty. */
  formKey: number;
}

interface SimulateActions {
  open: () => void;
  close: () => void;
  setMasterOrderField: <K extends keyof MasterOrderInfo.AsObject>(key: K, value: MasterOrderInfo.AsObject[K]) => void;
  setCustomSlaveSymbol: (v: string) => void;
  submit: () => Promise<void>;
  resetForm: () => void;
  /** Back to the form from a result: clears the result, keeps the order. */
  editOrder: () => void;
}

interface SimulateResult {
  status: ReturnType<typeof useSimulateTrade>['status'];
  data: ReturnType<typeof useSimulateTrade>['data'];
  logs: ReturnType<typeof useSimulateTrade>['logs'];
  error: ReturnType<typeof useSimulateTrade>['error'];
  loading: boolean;
}

const StateCtx = createContext<SimulateState | null>(null);
const ActionsCtx = createContext<SimulateActions | null>(null);
const MetaCtx = createContext<SimulateMeta | null>(null);
const ResultCtx = createContext<SimulateResult | null>(null);

interface ProviderProps extends SimulateMeta {
  children: ReactNode;
}

export const SimulateTradeProvider = ({
  masterAccountId,
  slaveAccountId,
  slaveId,
  masterSymbolOptions,
  masterAccount,
  slaveAccount,
  getSlaveConfigDraft,
  children
}: ProviderProps) => {
  const [isOpen, setOpen] = useState(false);
  const [masterOrder, setMasterOrder] = useState<MasterOrderInfo.AsObject>(DEFAULT_MASTER_ORDER);
  const [customSlaveSymbol, setCustomSlaveSymbolState] = useState('');
  const [formKey, setFormKey] = useState(0);

  const draftRef = useRef(getSlaveConfigDraft);
  useEffect(() => {
    draftRef.current = getSlaveConfigDraft;
  }, [getSlaveConfigDraft]);
  const stableGetDraft = useCallback(() => draftRef.current(), []);

  const { simulate, reset, status, data, logs, error, loading } = useSimulateTrade();

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  const setMasterOrderField = useCallback(<K extends keyof MasterOrderInfo.AsObject>(key: K, value: MasterOrderInfo.AsObject[K]) => {
    setMasterOrder((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setCustomSlaveSymbol = useCallback((v: string) => setCustomSlaveSymbolState(v), []);

  const resetForm = useCallback(() => {
    setMasterOrder(DEFAULT_MASTER_ORDER);
    setCustomSlaveSymbolState('');
    setFormKey((k) => k + 1);
    reset();
  }, [reset]);

  const submit = useCallback(async () => {
    if (!masterAccountId || !slaveAccountId) return;
    await simulate({
      masterAccountId,
      slaveAccountId,
      slaveConfig: stableGetDraft(),
      masterOrder,
      slaveSymbol: customSlaveSymbol
    });
  }, [simulate, masterAccountId, slaveAccountId, stableGetDraft, masterOrder, customSlaveSymbol]);

  const stateValue = useMemo<SimulateState>(
    () => ({ isOpen, masterOrder, customSlaveSymbol, formKey }),
    [isOpen, masterOrder, customSlaveSymbol, formKey]
  );

  const actionsValue = useMemo<SimulateActions>(
    () => ({ open, close, setMasterOrderField, setCustomSlaveSymbol, submit, resetForm, editOrder: reset }),
    [open, close, setMasterOrderField, setCustomSlaveSymbol, submit, resetForm, reset]
  );

  const metaValue = useMemo<SimulateMeta>(
    () => ({
      masterAccountId,
      slaveAccountId,
      slaveId,
      masterSymbolOptions,
      masterAccount,
      slaveAccount,
      getSlaveConfigDraft: stableGetDraft
    }),
    [masterAccountId, slaveAccountId, slaveId, masterSymbolOptions, masterAccount, slaveAccount, stableGetDraft]
  );

  const resultValue = useMemo<SimulateResult>(() => ({ status, data, logs, error, loading }), [status, data, logs, error, loading]);

  return (
    <MetaCtx.Provider value={metaValue}>
      <ActionsCtx.Provider value={actionsValue}>
        <StateCtx.Provider value={stateValue}>
          <ResultCtx.Provider value={resultValue}>{children}</ResultCtx.Provider>
        </StateCtx.Provider>
      </ActionsCtx.Provider>
    </MetaCtx.Provider>
  );
};

const useRequiredCtx = <T,>(ctx: Context<T | null>, name: string): T => {
  const value = useContext(ctx);
  if (value === null) {
    throw new Error(`${name} must be used inside <SimulateTradeProvider>`);
  }
  return value;
};

export const useSimulateState = () => useRequiredCtx(StateCtx, 'useSimulateState');
export const useSimulateActions = () => useRequiredCtx(ActionsCtx, 'useSimulateActions');
export const useSimulateMeta = () => useRequiredCtx(MetaCtx, 'useSimulateMeta');
export const useSimulateResult = () => useRequiredCtx(ResultCtx, 'useSimulateResult');
